// Workflow graph model.
//
// The persisted shape is `{ nodes: [{ id, type, params, position }],
// edges: [{ id, source, target }] }`. These helpers convert it to and from
// the shape React Flow renders and keep it consistent while editing.

export const FLOW_NODE_TYPE = 'analyticsNode';
export const SOURCE_TYPE = 'source';

let sequence = 0;

export function nextId(prefix = 'n') {
  sequence += 1;
  return `${prefix}-${Date.now().toString(36)}-${sequence.toString(36)}`;
}

export function makeNode(type, params = {}, position = { x: 60, y: 60 }) {
  return { id: nextId(type), type, params, position };
}

export function makeEdge(source, target) {
  return { id: `e-${source}-${target}`, source, target };
}

export function metasByKey(metas = []) {
  return (metas || []).reduce((acc, meta) => ({ ...acc, [meta.id]: meta }), {});
}

export function emptyGraph() {
  return { nodes: [], edges: [] };
}

// ---- conversions -------------------------------------------------------

export function toFlow(graph, { metas = {}, results = {}, selectedId = null } = {}) {
  const nodes = ((graph && graph.nodes) || []).map((node) => ({
    id: node.id,
    type: FLOW_NODE_TYPE,
    position: node.position || { x: 0, y: 0 },
    draggable: true,
    selected: node.id === selectedId,
    data: {
      nodeType: node.type,
      params: node.params || {},
      meta: metas[node.type],
      result: results[node.id] || null
    }
  }));
  const edges = ((graph && graph.edges) || []).map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target
  }));
  return { nodes, edges };
}

export function fromFlow(nodes, edges) {
  return {
    nodes: (nodes || []).map((node) => ({
      id: node.id,
      type: node.data.nodeType,
      params: node.data.params || {},
      position: { x: node.position.x, y: node.position.y }
    })),
    edges: (edges || []).map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target
    }))
  };
}

// ---- edits -------------------------------------------------------------

export function addNode(graph, node, fromId = null) {
  const connectFrom = fromId && fromId !== node.id ? fromId : null;
  const edges = connectFrom ? [...graph.edges, makeEdge(connectFrom, node.id)] : graph.edges;
  return { nodes: [...graph.nodes, node], edges };
}

export function removeNode(graph, id) {
  return {
    nodes: graph.nodes.filter((node) => node.id !== id),
    edges: graph.edges.filter((edge) => edge.source !== id && edge.target !== id)
  };
}

export function connect(graph, source, target) {
  const valid =
    source !== target && !graph.edges.some((e) => e.source === source && e.target === target);
  return valid ? { ...graph, edges: [...graph.edges, makeEdge(source, target)] } : graph;
}

export function updateParams(graph, id, params) {
  return {
    ...graph,
    nodes: graph.nodes.map((node) => (node.id === id ? { ...node, params } : node))
  };
}

export function moveNode(graph, id, position) {
  return {
    ...graph,
    nodes: graph.nodes.map((node) => (node.id === id ? { ...node, position } : node))
  };
}

// ---- reads -------------------------------------------------------------

export function findNode(graph, id) {
  return ((graph && graph.nodes) || []).find((node) => node.id === id) || null;
}

export function predecessors(graph, id) {
  return ((graph && graph.edges) || [])
    .filter((edge) => edge.target === id)
    .map((edge) => edge.source);
}

export function resultOf(result, id) {
  return (result && result[id]) || null;
}

export function outputResultOf(result, id) {
  return (result && result.nodes[id]) || null;
}

export function outputNodes(graph, metas = {}) {
  return ((graph && graph.nodes) || []).filter(
    (node) => metas[node.type] && metas[node.type].terminal
  );
}

// Field keys offered to a node: the union of the schemas of its inputs.
export function inputFields(result, graph, id) {
  const seen = {};
  const fields = [];
  predecessors(graph, id).forEach((sourceId) => {
    (outputResultOf(result, sourceId)?.fields || []).forEach((field) => {
      if (!seen[field.key]) {
        seen[field.key] = true;
        fields.push(field);
      }
    });
  });
  return fields;
}

export const STATUS_LABELS = {
  pending: 'Not run',
  running: 'Running',
  done: 'Done',
  failed: 'Failed'
};

// Only an actual run occupies the workflow: a graph that was never run (or
// whose run finished) can always be started again.
export function isRunning(status) {
  return status === 'running';
}
